import { DepartmentPageProps } from "/Models/Generated/DepartmentPageProps";
import "./DepartmentPage.scss";
import {
  ArrowRightIcon,
  ContentArea,
  GridRow,
  HeroBasic,
  GridPresetMainContent,
  GridPresetSidebar,
  ContactInfoCardBlock,
  PersonCardsBlock,
  NumberedListArticle,
  StaffInfoCardBlock,
  ListSections,
  Link,
  HideFor,
  OptimizelyEditProperty,
} from "/App.Components";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";
import { ContentAreaItemMargin } from "/Components/Shared/ContentAreaItem/ContentAreaItem";
import { FocusedLinksBlockProps } from "/Models/Generated/FocusedLinksBlockProps";

const DepartmentPage = ({
  pageHero,
  responsibilityText,
  goalResponsibilities,
  goalResponsibilitiesPropertyName,
  contactInfo,
  contactInfoPropertyName,
  contactPersons,
  staffInfo,
  staffInfoPropertyName,
  sections,
  sidebar,
}: DepartmentPageProps) => {
  const hasGoals = (goalResponsibilities?.length ?? 0) > 0;
  const hasResponsibilities = Boolean(
    responsibilityText || hasGoals || goalResponsibilitiesPropertyName,
  );

  const mobileNavigationItems = (sidebar?.items ?? []).filter(
    (item) =>
      item.name === "FocusedLinksBlock" &&
      (item.component as FocusedLinksBlockProps | undefined)
        ?.showAsMobileNavigation,
  );

  const mobileNavigationLinks = (
    <div className="department-page-norad__mobile-navigation-links">
      {mobileNavigationItems.map((item) => {
        const focusedLinks = item.component as FocusedLinksBlockProps;
        const links = [
          ...(focusedLinks.links ?? []),
          ...(focusedLinks.specialLinks ?? []),
        ];

        return (
          <ul key={item.blockId}>
            {links.map((link, index) => (
              <li key={`${item.blockId}-${index}`}>
                <Link
                  {...link}
                  isExternal={false}
                  iconPlacement="before"
                  icon={ArrowRightIcon}
                  size="x-small"
                  fixedIcon
                />
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );

  return (
    <div className="department-page-norad">
      {pageHero && <HeroBasic {...pageHero} />}
      {mobileNavigationItems.length > 0 && (
        <HideFor desktop desktopMedium desktopLargeUp>
          <div className="department-page-norad__mobile-navigation-links--top">
            <GridRow>
              <GridPresetMainContent>
                {mobileNavigationLinks}
              </GridPresetMainContent>
            </GridRow>
          </div>
        </HideFor>
      )}
      <GridRow>
        <GridPresetMainContent>
          <div className="department-page-norad__main-content">
            <div
              id="staff-info"
              className="department-page-norad__staff-info-anchor"
            >
              {contactPersons && <PersonCardsBlock {...contactPersons} />}
              {!contactPersons?.personCards?.length && (
                <OptimizelyEditProperty
                  propertyName={staffInfoPropertyName}
                  clientRender
                >
                  {staffInfo && <StaffInfoCardBlock {...staffInfo} />}
                </OptimizelyEditProperty>
              )}
            </div>
            <OptimizelyEditProperty
              propertyName={contactInfoPropertyName}
              clientRender
            >
              {contactInfo && (
                <ContactInfoCardBlock {...contactInfo} inlineLabels />
              )}
            </OptimizelyEditProperty>
            {hasResponsibilities && (
              <div className="department-page-norad__responsibilities">
                {responsibilityText && (
                  <p className="department-page-norad__list-lead-text">
                    {responsibilityText}
                  </p>
                )}
                <OptimizelyEditProperty
                  propertyName={goalResponsibilitiesPropertyName}
                  clientRender
                >
                  {hasGoals && (
                    <NumberedListArticle>
                      {goalResponsibilities.map((item, idx) => (
                        <li key={idx} value={item.goalNumber}>
                          {item.definition}
                        </li>
                      ))}
                    </NumberedListArticle>
                  )}
                </OptimizelyEditProperty>
              </div>
            )}
            {sections && <ListSections {...sections} />}
          </div>
        </GridPresetMainContent>
        {(sidebar?.propertyName || ContentArea.HasContent(sidebar)) && (
          <GridPresetSidebar>
            <aside className="department-page-norad__sidebar">
              <ContentArea
                {...sidebar}
                contentAreaType={ContentAreaType.OneThird}
                margin={ContentAreaItemMargin.Sidebar}
              />
            </aside>
          </GridPresetSidebar>
        )}
      </GridRow>
    </div>
  );
};

export default DepartmentPage;
